/**
 * THI-267 — client-side mirror of `deriveClassName` /
 * `apps/api/src/services/classGroupService.ts` (the ONE implementation of
 * the derived name lives there, spec §6/plan Global Constraint 21). This
 * copy exists only to render a live PREVIEW placeholder in `ClassFormSheet`
 * before the class exists server-side (the "Để trống sẽ hiện Piano · T4
 * 18:00" placeholder, handoff README "Copy mới") — it is never sent to the
 * API and never used to decide what a class is actually called; the server
 * always computes the authoritative `displayName` on write.
 *
 * Keep this in lockstep with the server function by hand (same dayOfWeek
 * convention 1=Mon..7=Sun, same weekday-label table, same rules); Task 18's
 * `classDisplay.test.ts` covers it.
 */

const DAY_LABELS = ['', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

/** One slot -> "Toán · T4 18:00"; two or more -> "Toán · T2, T4, T6" (ascending,
 *  deduped weekdays, no time once there is more than one slot). No slots at
 *  all -> the bare subject name. Mirrors `deriveClassName` server-side. */
export function deriveClassName(subjectName: string, slots: { dayOfWeek: number; startTime: string }[]): string {
  if (slots.length === 0) return subjectName;
  if (slots.length === 1) {
    const s = slots[0]!;
    return `${subjectName} · ${DAY_LABELS[s.dayOfWeek]} ${s.startTime}`;
  }
  const days = Array.from(new Set(slots.map((s) => s.dayOfWeek))).sort((a, b) => a - b);
  return `${subjectName} · ${days.map((d) => DAY_LABELS[d]).join(', ')}`;
}

/** Whether a roster row's khung giờ membership is PARTIAL: the student sits in
 *  some, but not all, of the class's khung giờ. Zero joined is not partial (the
 *  row is not in the class at all), and joined === total is not partial either,
 *  so the "2/3 khung giờ" note renders for exactly the rows that need it.
 *
 *  The rule lives here rather than inline in `ClassDetailPage` because it is a
 *  boundary rule with two easy-to-get-wrong ends, and the text it gates is
 *  built from the i18n string `classDetail.roster.partial` ("{{joined}}/{{total}}
 *  khung giờ") — the copy stays in the i18n files, only the decision is here.
 */
export function isPartialMembership(joined: number, total: number): boolean {
  return joined > 0 && joined < total;
}
